import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Phone,
  PlayCircle,
  XCircle,
  ArrowRight,
} from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { StatusPill } from "@/components/uiext";
import { toast } from "sonner";

/**
 * DialTester — read-only "what would happen if I dialed X?" simulator.
 *
 * Props:
 *   - customers: array of org users (for the optional "as user" picker)
 *
 * Talks to: POST /api/routing/simulate
 * Sends NO SIP traffic. Pure decision visualization.
 */
export function DialTester({ customers = [] }) {
  const { t } = useTranslation();
  const [number, setNumber] = useState("");
  const [direction, setDirection] = useState("outbound");
  const [userId, setUserId] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [showAll, setShowAll] = useState(false);

  const run = async () => {
    if (!number.trim()) {
      toast.error(t("dialTester.errNoNumber", "Enter a number to test"));
      return;
    }
    setBusy(true);
    try {
      const r = await api.post("/routing/simulate", {
        number: number.trim(),
        direction,
        user_id: userId || null,
      });
      setResult(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("common.failed", "Failed"));
    } finally {
      setBusy(false);
    }
  };

  const verdictKind =
    result?.verdict === "match" ? "ok" : "danger";
  const VerdictIcon =
    result?.verdict === "match" ? CheckCircle2 : XCircle;

  return (
    <Card
      className="mb-6 border-[hsl(var(--accent))]/30"
      data-testid="dial-tester-card"
    >
      <CardContent className="p-4 space-y-4">
        <div className="flex items-center gap-2">
          <Phone className="h-4 w-4 text-[hsl(var(--accent))]" />
          <span className="text-sm font-semibold">
            {t("dialTester.title", "Dial Tester")}
          </span>
          <span className="text-[11px] text-muted-foreground">
            {t(
              "dialTester.subtitle",
              "Simulate a routing decision without sending any SIP traffic."
            )}
          </span>
        </div>

        {/* Phase 6n — Responsive grid layout fix.
            On iPad portrait (IMG_0290) the `md:grid-cols-[1fr_180px_180px_auto]`
            layout clipped the Test button at the right edge because the
            `auto` column couldn't measure itself inside the constrained
            parent. New breakpoints: 1 col on mobile, 2 cols on sm/md
            (number + direction row 1, user + Test row 2), 4 cols on lg+. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-[1fr_180px_180px_auto] gap-3 items-end">
          <div className="col-span-1 sm:col-span-2 lg:col-span-1">
            <Label className="text-xs">
              {t("dialTester.number", "Dialed number")}
            </Label>
            <Input
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              placeholder="e.g. 18005551212"
              data-testid="dial-tester-number"
              onKeyDown={(e) => {
                if (e.key === "Enter") run();
              }}
            />
          </div>
          <div>
            <Label className="text-xs">
              {t("dialTester.direction", "Direction")}
            </Label>
            <Select value={direction} onValueChange={setDirection}>
              <SelectTrigger data-testid="dial-tester-direction">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="outbound">
                  {t("dialTester.outbound", "Outbound")}
                </SelectItem>
                <SelectItem value="inbound">
                  {t("dialTester.inbound", "Inbound")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">
              {t("dialTester.user", "As user (optional)")}
            </Label>
            <Select
              value={userId || "any"}
              onValueChange={(v) => setUserId(v === "any" ? "" : v)}
            >
              <SelectTrigger data-testid="dial-tester-user">
                <SelectValue placeholder={t("dialTester.anyUser", "Any user")} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="any">
                  {t("dialTester.anyUser", "Any user")}
                </SelectItem>
                {customers.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name || c.id}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={run}
            disabled={busy}
            data-testid="dial-tester-run"
            className="w-full sm:w-auto col-span-1 sm:col-span-2 lg:col-span-1"
          >
            <PlayCircle className="h-4 w-4 mr-1" />
            {busy ? t("common.loading", "Loading…") : t("dialTester.run", "Test")}
          </Button>
        </div>

        {result && (
          <div className="space-y-3 pt-2" data-testid="dial-tester-result">
            <div className="flex items-start gap-2">
              <StatusPill kind={verdictKind}>
                <VerdictIcon className="h-3 w-3 mr-1 inline" />
                {result.verdict === "match"
                  ? t("dialTester.match", "MATCH")
                  : t("dialTester.noMatch", "NO MATCH")}
              </StatusPill>
              <p className="text-xs text-muted-foreground">
                {result.verdict_text}
              </p>
            </div>

            {result.user && (
              <div
                className="rounded-md border border-border bg-muted/30 px-3 py-2 text-[11px] font-mono"
                data-testid="dial-tester-user-info"
              >
                <span className="text-muted-foreground">user:</span>{" "}
                <span className="font-semibold">{result.user.name}</span>
                {result.user.matched_via_did && (
                  <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))] text-[10px]">
                    {t("dialTester.viaDid", "matched via DID")}
                  </span>
                )}
                {result.user.default_cid && (
                  <span className="ml-2 text-muted-foreground">
                    cid={result.user.default_cid}
                  </span>
                )}
                {result.user.passthrough_far_end_cid && (
                  <span className="ml-2 text-[hsl(var(--info))]">
                    {t("dialTester.passthroughCid", "PBX From passthrough ON")}
                  </span>
                )}
              </div>
            )}

            {result.user_lookup_note && (
              <p className="text-[11px] text-[hsl(var(--warn))]">
                {result.user_lookup_note}
              </p>
            )}

            {result.winning_route && (
              <div
                className="rounded-md border border-[hsl(var(--ok))]/30 bg-[hsl(var(--ok))]/5 px-3 py-2 space-y-1.5 text-xs"
                data-testid="dial-tester-route"
              >
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-muted-foreground">
                    {t("dialTester.route", "Route")}:
                  </span>
                  <span className="font-semibold">
                    {result.winning_route.name}
                  </span>
                  <span className="font-mono text-muted-foreground">
                    prefix='{result.winning_route.prefix || "(any)"}'
                  </span>
                  <span className="text-muted-foreground">
                    prio {result.winning_route.priority}
                  </span>
                </div>

                {result.transform && (
                  <div className="flex flex-wrap items-center gap-2 font-mono">
                    <span className="text-muted-foreground">
                      {t("dialTester.transform", "Transform")}:
                    </span>
                    <span>{result.transform.input}</span>
                    {(result.transform.prefix_strip > 0 ||
                      result.transform.prefix_add) && (
                      <>
                        <ArrowRight className="h-3 w-3 text-muted-foreground" />
                        {result.transform.prefix_strip > 0 && (
                          <span className="text-[hsl(var(--warn))]">
                            strip {result.transform.prefix_strip}
                          </span>
                        )}
                        {result.transform.prefix_add && (
                          <span className="text-[hsl(var(--info))]">
                            +{result.transform.prefix_add}
                          </span>
                        )}
                      </>
                    )}
                    <ArrowRight className="h-3 w-3 text-muted-foreground" />
                    <span className="text-[hsl(var(--accent))] font-semibold">
                      {result.transform.result}
                    </span>
                  </div>
                )}
              </div>
            )}

            {result.failover_chain && result.failover_chain.length > 0 && (
              <div
                className="rounded-md border border-border bg-card px-3 py-2 space-y-1.5"
                data-testid="dial-tester-failover"
              >
                <div className="text-xs font-semibold text-muted-foreground mb-1">
                  {t("dialTester.failover", "Trunk attempt order")}
                </div>
                <ol className="space-y-1.5">
                  {result.failover_chain.map((t) => (
                    <li
                      key={`${t.position}-${t.trunk_id}`}
                      className="flex items-center gap-2 text-xs"
                      data-testid={`dial-tester-trunk-${t.position}`}
                    >
                      <span className="font-mono text-[10px] w-5 text-center text-muted-foreground">
                        #{t.position + 1}
                      </span>
                      {t.ok ? (
                        <CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--ok))]" />
                      ) : (
                        <XCircle className="h-3.5 w-3.5 text-[hsl(var(--danger))]" />
                      )}
                      <span className="font-semibold">
                        {t.name || t.trunk_id || "(unknown)"}
                      </span>
                      {t.host && (
                        <span className="font-mono text-muted-foreground">
                          {t.host}:{t.port}/{t.transport}
                        </span>
                      )}
                      <span
                        className={`ml-auto text-[10px] uppercase tracking-wider ${
                          t.ok
                            ? "text-[hsl(var(--ok))]"
                            : "text-[hsl(var(--danger))]"
                        }`}
                      >
                        {t.reason}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            <div>
              <button
                type="button"
                onClick={() => setShowAll(!showAll)}
                className="text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                data-testid="dial-tester-toggle-considered"
              >
                {showAll ? (
                  <ChevronUp className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
                {t("dialTester.considered", "All routes considered")} (
                {result.considered_routes?.length || 0})
              </button>
              {showAll && (
                <div className="mt-2 space-y-1">
                  {(result.considered_routes || []).map((r) => (
                    <div
                      key={r.id}
                      className={`flex items-center gap-2 text-[11px] font-mono px-2 py-1 rounded ${
                        r.kept
                          ? "bg-[hsl(var(--ok))]/10 text-[hsl(var(--ok))]"
                          : "bg-muted/30 text-muted-foreground"
                      }`}
                    >
                      {r.kept ? (
                        <CheckCircle2 className="h-3 w-3 shrink-0" />
                      ) : (
                        <XCircle className="h-3 w-3 shrink-0" />
                      )}
                      <span className="font-semibold w-32 truncate">
                        {r.name}
                      </span>
                      <span className="text-muted-foreground">{r.note}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

export default DialTester;
